// src/Views/NavBar.js — Flasher sticky-blur nav with scroll progress.
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { LogoMark, MagneticButton } from '../components/primitives';

// Route-based links so the nav works from detail pages too. Each route mounts
// HomePage with the matching scrollTo, which the home effect uses to scroll to
// the right section.
const NAV_LINKS = [
  { label: 'Builds', to: '/builds' },
  { label: 'Products', to: '/products' },
  { label: 'How I Think', to: '/how-i-think' },
  { label: 'Work With Me', to: '/work-with-me' },
  { label: 'About', to: '/who-i-am' },
];

const CALENDLY_URL = 'https://calendly.com/solomon-foshko-designbydialogue/30min';

const NavBar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [progress, setProgress] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 920 : false
  );

  // Load Calendly assets once so the popup CTA works.
  useEffect(() => {
    const existingLink = document.querySelector('link[href*="calendly"]');
    if (!existingLink) {
      const link = document.createElement('link');
      link.href = 'https://assets.calendly.com/assets/external/widget.css';
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
    const existingScript = document.querySelector('script[src*="calendly"]');
    if (!existingScript) {
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.type = 'text/javascript';
      script.async = true;
      document.head.appendChild(script);
    }
  }, []);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 24);
      const max = document.body.scrollHeight - window.innerHeight;
      setProgress(max > 0 ? window.scrollY / max : 0);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 920);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleSchedule = (e) => {
    e.preventDefault();
    if (window.Calendly && window.Calendly.initPopupWidget) {
      window.Calendly.initPopupWidget({ url: CALENDLY_URL });
    } else {
      // Fallback: open the page directly if the widget hasn't loaded yet.
      window.open(CALENDLY_URL, '_blank', 'noopener,noreferrer');
    }
  };

  const closeMobile = () => setMobileOpen(false);

  return (
    <>
      <nav
        style={{
          position: 'fixed',
          top: 0, left: 0, right: 0,
          zIndex: 100,
          padding: scrolled ? '12px 28px' : '20px 28px',
          transition: 'padding 0.25s ease',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            maxWidth: 1320,
            margin: '0 auto',
            padding: scrolled ? '10px 18px' : '12px 22px',
            borderRadius: 999,
            background: scrolled ? 'rgba(10, 6, 24, 0.65)' : 'rgba(10, 6, 24, 0.25)',
            border: '1px solid var(--line)',
            backdropFilter: 'blur(18px) saturate(160%)',
            WebkitBackdropFilter: 'blur(18px) saturate(160%)',
            transition: 'background 0.25s ease, padding 0.25s ease',
          }}
        >
          <Link
            to="/"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 12,
              fontFamily: 'var(--f-mono)',
              fontWeight: 600,
              fontSize: 13,
              letterSpacing: '0.04em',
              color: 'var(--ink)',
              textDecoration: 'none',
            }}
          >
            <LogoMark />
            <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.5 }}>
              <span>Design by Dialogue.AI</span>
              <span style={{ fontSize: 11, color: 'var(--ink-3)', fontWeight: 400 }}>
                Solomon Foshko · Builder
              </span>
            </span>
          </Link>

          {!isMobile && (
            <ul
              className="nav-desktop"
              style={{
                display: 'flex',
                listStyle: 'none',
                gap: 4,
                margin: 0,
                padding: 0,
                alignItems: 'center',
              }}
            >
              {NAV_LINKS.map((l) => (
                <li key={l.label}>
                  <Link
                    to={l.to}
                    style={{
                      display: 'inline-block',
                      padding: '8px 14px',
                      fontSize: 14,
                      fontWeight: 500,
                      color: 'var(--ink-2)',
                      borderRadius: 999,
                      textDecoration: 'none',
                      transition: 'color 0.2s, background 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = 'var(--ink)';
                      e.currentTarget.style.background = 'rgba(160,102,255,0.12)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = 'var(--ink-2)';
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
              <li style={{ marginLeft: 8 }}>
                <MagneticButton size="sm" accent="magenta" onClick={handleSchedule}>
                  Schedule <span style={{ opacity: 0.7 }}>↗</span>
                </MagneticButton>
              </li>
            </ul>
          )}

          {isMobile && (
            <button
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen((v) => !v)}
              style={{
                display: 'inline-flex',
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid var(--line)',
                color: 'var(--ink)',
                width: 40, height: 40,
                borderRadius: 12,
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              {mobileOpen ? '✕' : '≡'}
            </button>
          )}
        </div>

        {/* Scroll progress bar */}
        <div
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0,
            height: 2,
            background: 'transparent',
            zIndex: 101,
            pointerEvents: 'none',
          }}
        >
          <div
            style={{
              width: `${progress * 100}%`,
              height: '100%',
              background: 'linear-gradient(90deg, var(--purple), var(--magenta), var(--cyan))',
              boxShadow: '0 0 12px var(--magenta)',
              transition: 'width 0.05s linear',
            }}
          />
        </div>

        {/* Mobile drawer */}
        {isMobile && mobileOpen && (
          <div
            style={{
              position: 'fixed',
              top: 80, left: 16, right: 16,
              padding: 12,
              background: 'rgba(10, 6, 24, 0.95)',
              border: '1px solid var(--line)',
              borderRadius: 20,
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              zIndex: 100,
            }}
          >
            {NAV_LINKS.map((l) => (
              <Link
                key={l.label}
                to={l.to}
                onClick={closeMobile}
                style={{
                  display: 'block',
                  padding: '14px 18px',
                  fontSize: 16,
                  color: 'var(--ink)',
                  borderRadius: 12,
                  textDecoration: 'none',
                }}
              >
                {l.label}
              </Link>
            ))}
            <button
              onClick={(e) => { handleSchedule(e); closeMobile(); }}
              style={{
                marginTop: 8,
                width: '100%',
                padding: '14px 18px',
                fontSize: 16,
                color: '#0a0419',
                background: 'linear-gradient(135deg, var(--magenta) 0%, var(--purple) 100%)',
                border: 'none',
                borderRadius: 12,
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              Schedule ↗
            </button>
          </div>
        )}
      </nav>
    </>
  );
};

export default NavBar;
